import type { Table } from 'dexie'
import { makeAutoObservable } from 'mobx'

import { DisposeBag } from '@src/lib/DisposeBag/DisposeBag'
import { logError } from '@src/lib/log'
import type WorkflowStore from '@src/service/WorkflowStore'
import type { AnalyticsStore } from '@src/service/analytics'
import type WorkflowDefinitionModel from '@src/service/model/workflow/WorkflowDefinitionModel'
import type StorageService from '@src/service/storage/StorageService'
import type Transport from '@src/service/transport'
import type {
  AgentResource,
  AgentResourceInput,
  AgentResourceUpdateInput,
} from '@src/service/transport/AiClient'
import {
  customActionToolDefinitionListSchema,
  type CustomActionToolDefinitionListInput,
  type NormalizedCustomActionToolDefinition,
} from '@src/service/transport/ai-client/aiCustomActionToolDefinitionSchema'
import type {
  AgentJobDefinitionOptimizationRequest,
  AgentJobDefinitionOptimizationResponse,
  AiCustomJobDefinitionV1CreateInput,
  AiCustomJobDefinitionV1UpdateInput,
  CodableAiCustomJobV1,
} from '@src/service/transport/ai-client/aiCustomJobDefinitionV1Schema'
import type { AiCustomJobTemplateId } from '@src/service/transport/ai-client/aiCustomJobTemplateV1Schema'
import type {
  AiTriggerDefinition,
  AiTriggerDefinitionByIdInput,
  AiTriggerDefinitionCreateInput,
  AiTriggerDefinitionListInput,
  AiTriggerDefinitionUpdateInput,
  AiTriggerDefinitionValidateExistingInput,
  AiTriggerDefinitionValidateInput,
  VoiceConfig,
  CodableAiTriggerDefinition,
  LanguageCode,
} from '@src/service/transport/ai-client/aiTriggerDefinitionSchema'
import type { AiTriggerDefinitionId } from '@src/service/transport/ai-client/idSchemas'
import { AI_TRIGGER_DEFINITION_TABLE_NAME } from '@src/service/worker/repository/AiTriggerDefinitionRepository'

type UpdateConversationHistoryEnabledProps = {
  enabled: boolean
  triggerDefinitionId: AiTriggerDefinitionId
  workflowDefinitionId: string
  blockId: string
}

type UpdateLanguagesProps = {
  languages: LanguageCode[]
  triggerDefinitionId: AiTriggerDefinitionId
  workflowDefinitionId: string
  blockId: string
}

type UpdateResourceAccessProps = {
  isAllowed: boolean
  resource: AgentResource
  triggerDefinitionId: AiTriggerDefinitionId
  workflowDefinitionId: string
  blockId: string
}

type UpdateCustomJobAccessProps = {
  isAllowed: boolean
  job: CodableAiCustomJobV1
  triggerDefinitionId: AiTriggerDefinitionId
  workflowDefinitionId: string
  blockId: string
}

export default class AutomationsStore {
  private readonly aiAgentTriggerDefinitionsTable: Table<CodableAiTriggerDefinition>
  private disposeBag = new DisposeBag()

  constructor(
    private storage: StorageService,
    private transport: Transport,
    private workflowStore: WorkflowStore,
    private analytics: AnalyticsStore,
  ) {
    this.aiAgentTriggerDefinitionsTable = this.storage.database.table(
      AI_TRIGGER_DEFINITION_TABLE_NAME,
    )
    this.subscribeToWebSocket()

    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    )
  }

  getResources() {
    return this.transport.ai.automations.getResources()
  }

  getResourceById(id: string) {
    return this.transport.ai.automations.getResourceById(id)
  }

  createResourcePage({ title, content, sources, format }: AgentResourceInput) {
    return this.transport.ai.automations.createResourcePage({
      title,
      content,
      sources,
      format,
    })
  }

  updateResourcePage({ id, title, content, sources }: AgentResourceUpdateInput) {
    return this.transport.ai.automations.updateResourcePage({
      id,
      title,
      content,
      sources,
    })
  }

  uploadResource(files: File[]) {
    return this.transport.ai.automations.uploadResource(files)
  }

  deleteResource(id: string) {
    return this.transport.ai.automations.deleteResource(id)
  }

  async getTriggerDefinitions(input: AiTriggerDefinitionListInput, signal?: AbortSignal) {
    // dedup local and remote definitions
    const triggerDefinitionsMap = new Map<
      AiTriggerDefinition['id'],
      CodableAiTriggerDefinition
    >()

    const localTriggerDefinitions = await this.aiAgentTriggerDefinitionsTable.toArray()

    for (const localTriggerDefinition of localTriggerDefinitions) {
      triggerDefinitionsMap.set(localTriggerDefinition.id, localTriggerDefinition)
    }

    const agentDefinitions = await this.transport.ai.agentDefinitions.getAll(
      input,
      signal,
    )

    for (const remoteDefinition of agentDefinitions.data) {
      triggerDefinitionsMap.set(remoteDefinition.id, remoteDefinition)
    }

    const triggerDefinitions = Array.from(triggerDefinitionsMap.values())
    await this.aiAgentTriggerDefinitionsTable.bulkPut(triggerDefinitions)
    return triggerDefinitions
  }

  async getTriggerDefinitionById(
    input: AiTriggerDefinitionByIdInput,
    signal?: AbortSignal,
  ) {
    const agentDefinition = await this.transport.ai.agentDefinitions.getById(
      input,
      signal,
    )
    await this.aiAgentTriggerDefinitionsTable.put(agentDefinition)
    return agentDefinition
  }

  async createTriggerDefinition(
    input: AiTriggerDefinitionCreateInput,
    signal?: AbortSignal,
  ) {
    const agentDefinition = await this.transport.ai.agentDefinitions.create(input, signal)
    await this.aiAgentTriggerDefinitionsTable.put(agentDefinition)
    return agentDefinition
  }

  async updateLocalTriggerDefinition(
    triggerDefinitionId: string,
    workflowDefinitionId: string,
    stepId: string,
    update: Partial<CodableAiTriggerDefinition>,
  ) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    if (!triggerDefinition) {
      return
    }

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      ...update,
    }

    await this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId,
      update: {},
      skipAddingToHistory: true,
    })
  }

  // Discard local Ai Agent trigger definitions for a workflow
  discardLocalAiAgentTriggerDefinitions(
    workflowSteps: WorkflowDefinitionModel['workflowSteps'],
  ) {
    const triggerDefinitionIds = Object.values(workflowSteps)
      .map((step) => {
        const triggerDefinitionId = step?.configuration?.find(
          (config) => config.variableKey === 'WVagentTriggerDefinitionId',
        )?.value as AiTriggerDefinitionId

        return triggerDefinitionId
      })
      .filter(Boolean)

    void Promise.all(
      triggerDefinitionIds.map((triggerId) =>
        this.discardLocalTriggerDefinition(triggerId),
      ),
    )
  }

  async discardLocalTriggerDefinition(triggerDefinitionId: AiTriggerDefinitionId) {
    const remoteAgentDefinition = await this.transport.ai.agentDefinitions.getById({
      id: triggerDefinitionId,
    })

    if (!remoteAgentDefinition) {
      // delete the local trigger definition
      await this.aiAgentTriggerDefinitionsTable.delete(triggerDefinitionId)
      return
    }

    // update the local trigger definition with the remote one
    await this.aiAgentTriggerDefinitionsTable.put(remoteAgentDefinition)
  }

  async updateTriggerDefinition(
    input: AiTriggerDefinitionUpdateInput,
    signal?: AbortSignal,
  ) {
    const agentDefinition = await this.transport.ai.agentDefinitions.update(input, signal)
    await this.aiAgentTriggerDefinitionsTable.put(agentDefinition)
    return agentDefinition
  }

  async updateConversationHistoryEnabled({
    enabled,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: UpdateConversationHistoryEnabledProps) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    if (!triggerDefinition) {
      throw new Error('Trigger definition not found')
    }

    const updatedTriggerDefinition: CodableAiTriggerDefinition = {
      ...triggerDefinition,
      overrideConfiguration: {
        ...triggerDefinition.overrideConfiguration,
        conversationHistoryEnabled: enabled,
        contactInfoEnabled: enabled,
      },
    }

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    this.analytics.aiAutomations.conversationHistoryToggled(enabled)

    await this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
  }

  async updateLanguages({
    languages,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: UpdateLanguagesProps) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    if (!triggerDefinition) {
      throw new Error('Trigger definition not found')
    }

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      languages,
    } as CodableAiTriggerDefinition

    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    await this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
  }

  async updateResourceAccess({
    isAllowed,
    resource,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: UpdateResourceAccessProps) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    const updatedResources = isAllowed
      ? [{ definitionId: resource.id }, ...(triggerDefinition?.resources || [])]
      : triggerDefinition?.resources.filter((r) => r.definitionId !== resource.id)

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      resources: updatedResources,
    } as CodableAiTriggerDefinition

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    if (!isAllowed) {
      this.analytics.aiAutomations.removeKnowledgePageAccess(resource.title)
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- FIXME: Fix this ESLint violation!
    return this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
  }

  async addResourceToTriggerDefinition({
    resourceId,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: {
    resourceId: AgentResource['id']
    triggerDefinitionId: AiTriggerDefinitionId
    workflowDefinitionId: string
    blockId: string
  }) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    if (!triggerDefinition) {
      throw new Error('Trigger definition not found')
    }

    // Check if the resource already exists to avoid duplicates
    const resourceIdAlreadyExists = triggerDefinition.resources.some(
      (r) => r.definitionId === resourceId,
    )

    if (resourceIdAlreadyExists) {
      return triggerDefinition
    }

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      resources: [
        {
          definitionId: resourceId,
        },
        ...triggerDefinition.resources,
      ],
    } as CodableAiTriggerDefinition

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    void this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
    return updatedTriggerDefinition
  }

  validateTriggerDefinition(
    input: AiTriggerDefinitionValidateInput,
    signal?: AbortSignal,
  ) {
    return this.transport.ai.agentDefinitions.validate(input, signal)
  }

  validateExistingTriggerDefinition(
    input: AiTriggerDefinitionValidateExistingInput,
    signal?: AbortSignal,
  ) {
    return this.transport.ai.agentDefinitions.validateExisting(input, signal)
  }

  getAllAgentActions() {
    return this.transport.ai.jobActions.getAll()
  }

  async getCustomActionToolDefinitions(
    input: CustomActionToolDefinitionListInput,
    signal?: AbortSignal,
  ) {
    // Bounded paginate; raise if Sona-compatible integrations grow beyond ~5 pages.
    const MAX_PAGES = 5
    const accumulatedData: NormalizedCustomActionToolDefinition[] = []
    let totalItems = 0
    let pageToken: string | undefined = input.pageToken

    for (let i = 0; i < MAX_PAGES; i += 1) {
      const raw = await this.transport.ai.customActionToolDefinitions.getPage(
        { ...input, pageToken },
        signal,
      )
      const parsed = customActionToolDefinitionListSchema.safeParse(raw)
      if (!parsed.success) {
        logError('Invalid custom-action-tool-definitions response', {
          error: parsed.error.message,
        })
        break
      }
      accumulatedData.push(...parsed.data.data)
      totalItems = parsed.data.totalItems
      if (!parsed.data.nextPageToken) {
        break
      }
      pageToken = parsed.data.nextPageToken
    }

    return {
      data: accumulatedData,
      totalItems,
      nextPageToken: undefined,
    }
  }

  getMarketplaceCollections(signal?: AbortSignal) {
    return this.transport.marketplace.customActionCollections.list(signal)
  }

  installMarketplaceCollection(marketplaceItemId: string, signal?: AbortSignal) {
    return this.transport.marketplace.customActionCollections.install(
      marketplaceItemId,
      signal,
    )
  }

  configureMarketplaceCollection(
    marketplaceItemId: string,
    fieldValues: Record<string, string>,
    signal?: AbortSignal,
  ) {
    return this.transport.marketplace.customActionCollections.configure(
      marketplaceItemId,
      { fieldValues },
      signal,
    )
  }

  uninstallMarketplaceCollection(marketplaceItemId: string, signal?: AbortSignal) {
    return this.transport.marketplace.customActionCollections.uninstall(
      marketplaceItemId,
      signal,
    )
  }

  getAllJobTemplates() {
    return this.transport.ai.jobTemplates.getAllTemplates()
  }

  getJobTemplateById(templateId: AiCustomJobTemplateId) {
    return this.transport.ai.jobTemplates.getTemplateById(templateId)
  }

  getCustomJobDefinitionsV1() {
    return this.transport.ai.customJobsV1.getAll()
  }

  createCustomJobDefinitionV1(input: AiCustomJobDefinitionV1CreateInput) {
    return this.transport.ai.customJobsV1.create(input)
  }

  updateCustomJobDefinitionV1(update: AiCustomJobDefinitionV1UpdateInput) {
    return this.transport.ai.customJobsV1.update(update)
  }

  deleteCustomJobDefinitionV1(id: string) {
    return this.transport.ai.customJobsV1.delete(id)
  }

  optimizeCustomJobInstructions(
    input: AgentJobDefinitionOptimizationRequest,
    signal?: AbortSignal,
  ): Promise<AgentJobDefinitionOptimizationResponse> {
    return this.transport.ai.customJobsV1.optimize(input, signal)
  }

  createDefaultWorkflow(
    agentDefinitionId: string,
    phoneNumberId: string,
    greeting: string,
    isTest?: boolean,
    voiceConfig?: VoiceConfig,
  ) {
    return this.transport.voice.workflow.createDefaultAiAgentWorkflow(
      agentDefinitionId,
      phoneNumberId,
      greeting,
      isTest,
      voiceConfig,
    )
  }

  async updateCustomJobAccess({
    isAllowed,
    job,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: UpdateCustomJobAccessProps) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    const updatedJobs = isAllowed
      ? [{ definitionId: job.id }, ...(triggerDefinition?.jobs || [])]
      : triggerDefinition?.jobs.filter((j) => j.definitionId !== job.id)

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      jobs: updatedJobs,
    } as CodableAiTriggerDefinition

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    if (isAllowed) {
      this.analytics.aiAutomations.addCustomJobAccess(job.name)
    } else {
      this.analytics.aiAutomations.removeCustomJobAccess(job.name)
    }

    await this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
    return updatedTriggerDefinition
  }

  async addCustomJobToLocalTriggerDefinition({
    jobId,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: {
    jobId: CodableAiCustomJobV1['id']
    triggerDefinitionId: AiTriggerDefinitionId
    workflowDefinitionId: string
    blockId: string
  }) {
    const triggerDefinition =
      await this.aiAgentTriggerDefinitionsTable.get(triggerDefinitionId)

    if (!triggerDefinition) {
      throw new Error('Trigger definition not found')
    }

    // Check if the job already exists to avoid duplicates
    const jobsFromTriggerDefinition = triggerDefinition.jobs
    const jobIdAlreadyExists = jobsFromTriggerDefinition.some(
      (j) => j.definitionId === jobId,
    )

    if (jobIdAlreadyExists) {
      return triggerDefinition
    }

    const updatedTriggerDefinition = {
      ...triggerDefinition,
      jobs: [
        {
          definitionId: jobId,
        },
        ...jobsFromTriggerDefinition,
      ],
    }

    // Update the step to put the workflow in the draft/unpublished changes state
    this.workflowStore.updateNode({
      definitionId: workflowDefinitionId,
      stepId: blockId,
      update: {},
      skipAddingToHistory: true,
    })

    void this.aiAgentTriggerDefinitionsTable.put(updatedTriggerDefinition)
    return updatedTriggerDefinition
  }

  /**
   * Count the number of call flows (inboxes) in which the specified resource is attached.
   *
   * @param currentEntityId - The ID of the current phone number.
   * @param resourceId - The identifier of the resource to check.
   * @returns The number of call flows that have the resource attached.
   */
  async getResourceUsageCountInCallflows(currentEntityId: string, resourceId: string) {
    const entityIdsChecked = new Set<string>()

    const count = await this.aiAgentTriggerDefinitionsTable
      .filter((triggerDefinition) => {
        const hasResource = triggerDefinition.resources.some(
          (r) => r.definitionId === resourceId,
        )

        if (hasResource && !entityIdsChecked.has(triggerDefinition.entityId)) {
          entityIdsChecked.add(triggerDefinition.entityId)
          return true
        }

        return false
      })
      .count()

    if (currentEntityId && entityIdsChecked.has(currentEntityId)) {
      return count - 1
    }
    return count
  }

  get localTriggerDefinitions() {
    return this.aiAgentTriggerDefinitionsTable.toArray()
  }

  async getLocalTriggerDefinitionsById() {
    const localDefinitions = await this.localTriggerDefinitions
    return localDefinitions.reduce(
      (acc, triggerDefinition) => {
        acc[triggerDefinition.id] = triggerDefinition
        return acc
      },
      {} as Record<AiTriggerDefinitionId, CodableAiTriggerDefinition>,
    )
  }

  getOrganizationKeyterms() {
    return this.transport.ai.organizationKeyterms.get()
  }
  updateOrganizationKeyterms(keyterms: string[]) {
    return this.transport.ai.organizationKeyterms.update(keyterms)
  }

  tearDown() {
    this.disposeBag.dispose()
  }

  subscribeToWebSocket() {
    this.transport.onNotificationData.subscribe((event) => {
      switch (event.type) {
        case 'agent-definition-create':
          void this.aiAgentTriggerDefinitionsTable.put(event.definition)
          return
        case 'agent-definition-update':
          void this.aiAgentTriggerDefinitionsTable.put(event.definition)
          return
        case 'agent-definition-delete':
          void this.aiAgentTriggerDefinitionsTable.delete(event.id)
          return
      }
    })
  }
}
